/**
 * Motor de animație particule de vânt — stil Windy/Ventusky.
 *
 * Principiu: mii de particule se mișcă pe canvas conform câmpului de vânt
 * (U, V) interpolat biliniar. Traseele se estompează gradual (fade).
 * Culoarea și grosimea variază cu viteza vântului.
 * Suportă mască România — particulele din afara granițelor sunt ignorate.
 */
import { isMaskActive, getMaskPolygons } from './render.js';
import { windColorCss as windColor } from './wind-palette.js';

function windLineWidth(speed) {
  if (speed < 2) return 1.0;
  if (speed < 5) return 1.4;
  if (speed < 10) return 1.8;
  if (speed < 15) return 2.2;
  return 2.6;
}

// ── Sistem de particule ─────────────────────────────────────────
export class WindParticleSystem {
  /**
   * @param {HTMLCanvasElement} canvas - Canvas overlay pe hartă
   * @param {maplibregl.Map} map - Instanța MapLibre
   */
  constructor(canvas, map) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.map = map;

    // Câmpul de vânt
    this.windU = null;
    this.windV = null;
    this.lats = null;
    this.lons = null;
    this.ny = 0;
    this.nx = 0;

    // Parametri particule
    this.numParticles = this._isMobile() ? 3000 : 8000;
    this.maxAge = 90;
    this.fadeOpacity = 0.96;
    this.speedFactor = 0.0003; // scale viteza → grad/frame

    // State
    this.particles = [];
    this.running = false;
    this._rafId = null;
    this._needsReset = false;

    // Resize observer
    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(this.canvas);
    this._resize();

    // Reset particles on map move/zoom
    this.map.on('move', () => { this._needsReset = true; });
  }

  _isMobile() {
    return window.innerWidth < 768;
  }

  _resize() {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    this.canvas.width = rect.width * dpr;
    this.canvas.height = rect.height * dpr;
    // Reset transform then scale for DPR
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this._w = rect.width;
    this._h = rect.height;
    this._needsReset = true;
  }

  /**
   * Setează câmpul de vânt pentru timestep-ul curent.
   * @param {Float32Array} u - Componenta U (est), [lat×lon]
   * @param {Float32Array} v - Componenta V (nord), [lat×lon]
   * @param {Float64Array|Float32Array} lats - Array latitudini
   * @param {Float64Array|Float32Array} lons - Array longitudini
   * @param {number[]} shape - [ny, nx]
   */
  setWindField(u, v, lats, lons, shape) {
    this.windU = u;
    this.windV = v;
    this.lats = lats;
    this.lons = lons;
    this.ny = shape[0];
    this.nx = shape[1];
    this._needsReset = true;
  }

  /**
   * Interpolează biliniar (U, V) la poziția (lng, lat).
   * @returns {{ u: number, v: number, speed: number } | null}
   */
  _interpolate(lng, lat) {
    const lonMin = Number(this.lons[0]);
    const lonMax = Number(this.lons[this.nx - 1]);
    const latMin = Number(this.lats[0]);
    const latMax = Number(this.lats[this.ny - 1]);

    if (lng < lonMin || lng > lonMax || lat < latMin || lat > latMax) return null;

    const x = (lng - lonMin) / (lonMax - lonMin) * (this.nx - 1);
    const y = (lat - latMin) / (latMax - latMin) * (this.ny - 1);

    const x0 = Math.floor(x), x1 = Math.min(x0 + 1, this.nx - 1);
    const y0 = Math.floor(y), y1 = Math.min(y0 + 1, this.ny - 1);
    const dx = x - x0, dy = y - y0;

    const u =
      this.windU[y0 * this.nx + x0] * (1 - dx) * (1 - dy) +
      this.windU[y0 * this.nx + x1] * dx * (1 - dy) +
      this.windU[y1 * this.nx + x0] * (1 - dx) * dy +
      this.windU[y1 * this.nx + x1] * dx * dy;

    const v =
      this.windV[y0 * this.nx + x0] * (1 - dx) * (1 - dy) +
      this.windV[y0 * this.nx + x1] * dx * (1 - dy) +
      this.windV[y1 * this.nx + x0] * (1 - dx) * dy +
      this.windV[y1 * this.nx + x1] * dx * dy;

    return { u, v, speed: Math.sqrt(u * u + v * v) };
  }

  /**
   * Creează o particulă la poziție aleatorie în viewport.
   * Când masca România e activă, generează puncte doar în interiorul granițelor.
   */
  _randomParticle() {
    const bounds = this.map.getBounds();

    if (isMaskActive() && getMaskPolygons()) {
      // Intersecție viewport cu bounding box România
      const west = Math.max(bounds.getWest(), 20.2);
      const east = Math.min(bounds.getEast(), 30.0);
      const south = Math.max(bounds.getSouth(), 43.6);
      const north = Math.min(bounds.getNorth(), 48.3);

      if (west < east && south < north) {
        // Retry până găsim un punct în interiorul granițelor
        for (let attempt = 0; attempt < 30; attempt++) {
          const lng = west + Math.random() * (east - west);
          const lat = south + Math.random() * (north - south);
          if (this._pointInMask(lng, lat)) {
            return {
              lng, lat,
              age: Math.floor(Math.random() * this.maxAge),
              prevX: -1, prevY: -1,
            };
          }
        }
        // Fallback: centru România
        return {
          lng: 24.5 + Math.random() * 2,
          lat: 45.0 + Math.random() * 1.5,
          age: Math.floor(Math.random() * this.maxAge),
          prevX: -1, prevY: -1,
        };
      }
    }

    return {
      lng: bounds.getWest() + Math.random() * (bounds.getEast() - bounds.getWest()),
      lat: bounds.getSouth() + Math.random() * (bounds.getNorth() - bounds.getSouth()),
      age: Math.floor(Math.random() * this.maxAge),
      prevX: -1, prevY: -1,
    };
  }

  /**
   * Resetează toate particulele.
   */
  _resetParticles() {
    this.particles = [];
    for (let i = 0; i < this.numParticles; i++) {
      this.particles.push(this._randomParticle());
    }
    // Clear canvas
    this.ctx.clearRect(0, 0, this._w, this._h);
    this._needsReset = false;
  }

  /**
   * Un frame de animație.
   */
  _frame() {
    if (!this.running || !this.windU) return;

    if (this._needsReset) {
      this._resetParticles();
    }

    const ctx = this.ctx;

    // Fade traseele anterioare
    const prev = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = 'destination-in';
    ctx.fillStyle = `rgba(0, 0, 0, ${this.fadeOpacity})`;
    ctx.fillRect(0, 0, this._w, this._h);
    ctx.globalCompositeOperation = prev;

    // Desenare particule — grupate pe culoare pentru eficiență
    const colorBuckets = new Map();

    for (const p of this.particles) {
      p.age++;

      // Reset particulă dacă e prea veche
      if (p.age >= this.maxAge) {
        Object.assign(p, this._randomParticle());
        p.age = 0;
        continue;
      }

      const wind = this._interpolate(p.lng, p.lat);
      if (!wind) {
        Object.assign(p, this._randomParticle());
        p.age = 0;
        continue;
      }

      // Poziție curentă pe ecran
      const pt = this.map.project([p.lng, p.lat]);
      const oldX = pt.x, oldY = pt.y;

      // Mișcă particula conform vântului
      const cosLat = Math.cos(p.lat * Math.PI / 180);
      p.lng += wind.u * this.speedFactor / Math.max(cosLat, 0.1);
      p.lat += wind.v * this.speedFactor;

      // Poziție nouă pe ecran
      const newPt = this.map.project([p.lng, p.lat]);

      // Verificare vizibilitate
      if (newPt.x < -10 || newPt.x > this._w + 10 || 
          newPt.y < -10 || newPt.y > this._h + 10) {
        Object.assign(p, this._randomParticle());
        p.age = 0;
        continue;
      }



      // Dacă avem poziția anterioară validă, desenăm linia
      if (p.prevX >= 0) {
        const col = windColor(wind.speed);
        const lw = windLineWidth(wind.speed);
        const key = col + '|' + lw;
        if (!colorBuckets.has(key)) {
          colorBuckets.set(key, { col, lw, lines: [] });
        }
        colorBuckets.get(key).lines.push(p.prevX, p.prevY, newPt.x, newPt.y);
      }

      p.prevX = newPt.x;
      p.prevY = newPt.y;
    }

    // Desenare batch pe culoare
    for (const { col, lw, lines } of colorBuckets.values()) {
      ctx.strokeStyle = col;
      ctx.lineWidth = lw;
      ctx.beginPath();
      for (let i = 0; i < lines.length; i += 4) {
        ctx.moveTo(lines[i], lines[i + 1]);
        ctx.lineTo(lines[i + 2], lines[i + 3]);
      }
      ctx.stroke();
    }

    this._rafId = requestAnimationFrame(() => this._frame());
  }

  /**
   * Verifică dacă un punct (lon, lat) este în interiorul granițelor României.
   * Ray casting algorithm cu optimizare bbox per ring.
   * @param {number} lng - Longitudine
   * @param {number} lat - Latitudine
   * @returns {boolean}
   */
  _pointInMask(lng, lat) {
    const rings = getMaskPolygons();
    if (!rings) return true;

    // Quick bbox check (România: ~20.2-30.0 lon, ~43.6-48.3 lat)
    if (lng < 20.2 || lng > 30.0 || lat < 43.6 || lat > 48.3) return false;

    // Lazy cache bounding boxes per ring
    if (!this._ringBboxes || this._ringBboxes.length !== rings.length) {
      this._ringBboxes = rings.map(ring => {
        let minLon = Infinity, maxLon = -Infinity;
        let minLat = Infinity, maxLat = -Infinity;
        for (const [lon, lat] of ring) {
          if (lon < minLon) minLon = lon;
          if (lon > maxLon) maxLon = lon;
          if (lat < minLat) minLat = lat;
          if (lat > maxLat) maxLat = lat;
        }
        return { minLon, maxLon, minLat, maxLat };
      });
    }

    for (let r = 0; r < rings.length; r++) {
      const bb = this._ringBboxes[r];
      // Skip ring dacă punctul e în afara bbox-ului
      if (lng < bb.minLon || lng > bb.maxLon || lat < bb.minLat || lat > bb.maxLat) continue;
      if (this._pointInRing(lng, lat, rings[r])) return true;
    }
    return false;
  }

  /**
   * Ray casting point-in-polygon pentru un singur ring.
   */
  _pointInRing(lng, lat, ring) {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0], yi = ring[i][1];
      const xj = ring[j][0], yj = ring[j][1];
      if (((yi > lat) !== (yj > lat)) &&
          (lng < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    return inside;
  }

  /**
   * Pornește animația.
   */
  start() {
    if (this.running) return;
    this.running = true;
    this.canvas.style.display = 'block';
    this._needsReset = true;
    this._frame();
  }

  /**
   * Oprește animația și curăță canvas-ul.
   */
  stop() {
    this.running = false;
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this.ctx.clearRect(0, 0, this._w, this._h);
    this.canvas.style.display = 'none';
  }

  /**
   * Distruge sistemul (cleanup).
   */
  destroy() {
    this.stop();
    this._resizeObserver.disconnect();
  }
}
